import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Space,
  Input,
  Typography,
  Dropdown,
  Menu,
  Spin,
  Breadcrumb,
  Checkbox,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  PlusOutlined,
  FolderFilled,
  ClearOutlined,
  DownloadOutlined,
  MailOutlined,
  HomeOutlined,
  FilePdfOutlined,
  FileOutlined,
  RobotOutlined,
  MergeCellsOutlined,
  EyeOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  fetchDrawingFolders,
  fetchDrawingFolder,
  deleteDrawingFolder,
  downloadDrawingFolder,
  notifyProjectUsersAboutDrawingFolder,
  moveDrawingFolder,
  updateDrawingFolder,
  mergeDrawingFolderPDFs,
  fetchDrawingFolderShare,
  shareDrawingFolder,
  unshareDrawingFolder,
} from "../services/drawingFoldersAPI";
import {
  fetchDrawings,
  deleteDrawing,
  moveDrawing,
} from "../services/drawingsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateDrawingFolderModal from "../components/CreateDrawingFolderModal";
import UpdateDrawingFolderModal from "../components/UpdateDrawingFolderModal";
import CreateDrawingModal from "../components/CreateDrawingModal";
import UpdateDrawingModal from "../components/UpdateDrawingModal";
import NotifyUsersModal from "../components/NotifyUsersModal";
import ShareFolderModal from "../components/ShareFolderModal";
import Highlighter from "react-highlight-words";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { formatDate, formatDateLocal } from "../utils/dateUtils";
import { exportDrawingsToPDF } from "../utils/pdfUtils";
import {
  getPreferredPageSize,
  savePreferredPageSize,
} from "../utils/paginationUtils";

const CHILD_FOLDERS_LIMIT = 100;

const DrawingFolders = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name;
  const userRole = selectedProject?.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const canCreateFolders = userRole !== "subcontractor";
  const canUploadDrawings = userRole !== "subcontractor";

  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] =
    useState(false);
  const [isUpdateFolderModalVisible, setIsUpdateFolderModalVisible] =
    useState(false);
  const [isNotifyUsersModalVisible, setIsNotifyUsersModalVisible] =
    useState(false);
  const [isCreateDrawingModalVisible, setIsCreateDrawingModalVisible] =
    useState(false);
  const [isUpdateDrawingModalVisible, setIsUpdateDrawingModalVisible] =
    useState(false);
  const [isAIMode, setIsAIMode] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderToNotify, setFolderToNotify] = useState(null);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({});
  const [drawingFilters, setDrawingFilters] = useState({});
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const parentFolderId = searchParams.get("folder") || null;
  const [tableKey, setTableKey] = useState(0);
  const [deletingFolderId, setDeletingFolderId] = useState(null);
  const [deletingDrawingId, setDeletingDrawingId] = useState(null);
  const [downloadingFolderId, setDownloadingFolderId] = useState(null);
  const [notifyingFolderId, setNotifyingFolderId] = useState(null);
  const [folderToShare, setFolderToShare] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [showLatestRevisionsOnly, setShowLatestRevisionsOnly] = useState(true);
  const [isMergingPDFs, setIsMergingPDFs] = useState(false);

  const [draggingIds, setDraggingIds] = useState([]);
  const [draggingDrawingId, setDraggingDrawingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const suppressClickRef = useRef(false);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("drawing_folders"),
    total: 0,
  });

  const isInsideFolder = !!parentFolderId;

  const previousProjectIdRef = useRef(null);
  const previousFolderIdRef = useRef(parentFolderId);

  // Keep folder in the URL so refresh stays inside the current directory.
  // Convert preview-return state into ?folder= once, then rebuild breadcrumb from parents.
  useEffect(() => {
    const restoreFolderId = location.state?.restoreFolderId;
    if (restoreFolderId) {
      navigate(
        {
          pathname: "/drawing-folders",
          search: `?folder=${encodeURIComponent(restoreFolderId)}`,
        },
        { replace: true, state: {} }
      );
      setPagination((prev) => ({ ...prev, current: 1 }));
      setFilters({});
      setDrawingFilters({});
      setSearchedColumn("");
      setTableKey((prev) => prev + 1);
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!projectId) return;
    if (previousProjectIdRef.current == null) {
      previousProjectIdRef.current = projectId;
      return;
    }
    if (previousProjectIdRef.current === projectId) return;
    previousProjectIdRef.current = projectId;
    setSearchParams({}, { replace: true });
    setBreadcrumbPath([]);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters({});
    setDrawingFilters({});
    setSearchedColumn("");
    setTableKey((prev) => prev + 1);
  }, [projectId, setSearchParams]);

  useEffect(() => {
    if (!parentFolderId || !projectId) {
      setBreadcrumbPath([]);
      return undefined;
    }

    let cancelled = false;

    const buildBreadcrumb = async () => {
      const path = [];
      let currentId = parentFolderId;
      const seen = new Set();
      while (currentId && !seen.has(currentId)) {
        seen.add(currentId);
        try {
          const folder = await fetchDrawingFolder(projectId, currentId);
          if (!folder?._id) break;
          path.unshift({ id: String(folder._id), name: folder.name });
          currentId = folder.parent_id || null;
        } catch {
          break;
        }
      }
      if (!cancelled) setBreadcrumbPath(path);
    };

    buildBreadcrumb();
    return () => {
      cancelled = true;
    };
  }, [parentFolderId, projectId]);

  const folderPage = isInsideFolder ? 1 : pagination.current;
  const folderPageSize = isInsideFolder
    ? CHILD_FOLDERS_LIMIT
    : pagination.pageSize;

  const {
    data: foldersResponse,
    isLoading: foldersLoading,
    isError: foldersError,
    error: foldersErrorDetails,
  } = useQuery({
    queryKey: [
      "drawingFolders",
      projectId,
      parentFolderId,
      folderPage,
      folderPageSize,
      filters,
      sortField === "name" ? sortField : isInsideFolder ? null : sortField,
      sortField === "name" || !isInsideFolder ? sortDirection : null,
    ],
    queryFn: () =>
      fetchDrawingFolders(
        projectId,
        folderPage,
        folderPageSize,
        filters,
        sortField === "name" || !isInsideFolder ? sortField : null,
        sortField === "name" || !isInsideFolder ? sortDirection : null,
        parentFolderId
      ),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });

  const {
    data: currentFolderData,
  } = useQuery({
    queryKey: ["drawingFolder", projectId, parentFolderId],
    queryFn: () => fetchDrawingFolder(projectId, parentFolderId),
    enabled: !!projectId && !!parentFolderId,
  });

  useEffect(() => {
    if (currentFolderData?.show_latest_revisions_only !== undefined) {
      setShowLatestRevisionsOnly(currentFolderData.show_latest_revisions_only);
    }
  }, [currentFolderData]);

  const drawingSortField =
    sortField === "revision_date" || sortField === "created_at"
      ? sortField
      : sortField === "name"
        ? "drawing_number"
        : null;

  const {
    data: drawingsResponse,
    isLoading: drawingsLoading,
    isError: drawingsError,
    error: drawingsErrorDetails,
  } = useQuery({
    queryKey: [
      "drawings",
      projectId,
      parentFolderId,
      pagination.current,
      pagination.pageSize,
      drawingFilters,
      showLatestRevisionsOnly,
      drawingSortField,
      sortDirection,
    ],
    queryFn: () =>
      fetchDrawings(projectId, parentFolderId, {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
        filters:
          Object.keys(drawingFilters).length > 0
            ? JSON.stringify(drawingFilters)
            : null,
        show_latest_revisions_only: showLatestRevisionsOnly,
        sort_field: drawingSortField,
        sort_order: sortDirection,
        include_annotations: false,
      }),
    enabled: !!projectId && !!parentFolderId,
    placeholderData: keepPreviousData,
    refetchInterval: isAIProcessing ? 5000 : false,
  });

  useEffect(() => {
    if (!isAIProcessing) return undefined;
    const timer = setTimeout(() => setIsAIProcessing(false), 3 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [isAIProcessing]);

  const foldersData = foldersResponse?.drawing_folders || [];
  const totalFolders = foldersResponse?.total || 0;
  const drawingsData = drawingsResponse?.drawings || [];
  const totalDrawings = drawingsResponse?.total || 0;

  const folderTableData = useMemo(
    () =>
      foldersData.map((folder) => ({
        ...folder,
        key: `folder-${folder._id}`,
        isFolder: true,
      })),
    [foldersData]
  );

  const drawingTableData = useMemo(
    () =>
      drawingsData.map((drawing) => ({
        ...drawing,
        key: `drawing-${drawing._id}`,
        isFolder: false,
        name:
          [drawing.drawing_number, drawing.title].filter(Boolean).join(" - ") ||
          drawing.title ||
          drawing.drawing_number ||
          "",
      })),
    [drawingsData]
  );

  // Root: folders only. Inside a folder: never mix folders into the drawings
  // dataSource — antd v5 re-slices mixed rows against totalDrawings and drops drawings.
  const tableData = isInsideFolder ? drawingTableData : folderTableData;

  useEffect(() => {
    setPagination((prev) => {
      const total = isInsideFolder ? totalDrawings : totalFolders;
      if (prev.total === total) return prev;
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize) || 1);
      return {
        ...prev,
        total,
        current: Math.min(prev.current, maxPage),
      };
    });
  }, [totalFolders, totalDrawings, isInsideFolder]);

  const resetFolderViewState = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilters({});
    setDrawingFilters({});
    setSearchedColumn("");
    setSortField(null);
    setSortDirection(null);
    setTableKey((prev) => prev + 1);
  };

  const resetToFolder = (folderId) => {
    setSearchParams(folderId ? { folder: String(folderId) } : {});
    previousFolderIdRef.current = folderId ? String(folderId) : null;
    resetFolderViewState();
  };

  // Back/Forward changes ?folder= without going through resetToFolder.
  useEffect(() => {
    const nextFolderId = parentFolderId ? String(parentFolderId) : null;
    if (previousFolderIdRef.current === nextFolderId) return;
    previousFolderIdRef.current = nextFolderId;
    resetFolderViewState();
  }, [parentFolderId]);

  const handleEnterFolder = (folder) => {
    if (suppressClickRef.current) return;
    resetToFolder(folder._id);
  };

  const handleOpenDrawing = (drawing) => {
    if (!parentFolderId || !drawing?._id) return;
    navigate(`/drawings/${parentFolderId}/${drawing._id}`, {
      state: {
        fromDrawingFolders: true,
        restoreFolderId: parentFolderId,
      },
    });
  };

  const handleRowClick = (record) => {
    if (suppressClickRef.current) return;
    if (record.isFolder) {
      handleEnterFolder(record);
    } else {
      handleOpenDrawing(record);
    }
  };

  useEffect(() => {
    const breadcrumbDropStyle = (targetId) =>
      dropTargetId === targetId
        ? {
            background: "#e6f4ff",
            borderRadius: 4,
            padding: "0 4px",
            outline: "2px solid #1677ff",
          }
        : {};

    const makeDroppableProps = (targetId) =>
      isOwnerOrManager
        ? {
            onDragOver: (e) => {
              const canDropDrawing =
                draggingDrawingId &&
                targetId !== "root" &&
                String(targetId) !== String(parentFolderId);
              const canDropFolder =
                draggingIds.length > 0 &&
                canDropOntoTarget(targetId, draggingIds);
              if (!canDropDrawing && !canDropFolder) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setDropTargetId(targetId);
            },
            onDragLeave: () => {
              setDropTargetId((prev) => (prev === targetId ? null : prev));
            },
            onDrop: (e) => handleMoveDrop(targetId, e),
          }
        : {};

    const items = [
      {
        title: (
          <span {...makeDroppableProps("root")} style={breadcrumbDropStyle("root")}>
            <Button
              type="link"
              icon={<HomeOutlined />}
              style={{ padding: 0, height: "auto" }}
              onClick={() => resetToFolder(null)}
            >
              Home
            </Button>
          </span>
        ),
      },
    ];

    breadcrumbPath.forEach((item, index) => {
      items.push({
        title: (
          <span
            {...makeDroppableProps(item.id)}
            style={breadcrumbDropStyle(item.id)}
          >
            <Button
              type="link"
              style={{ padding: 0, height: "auto" }}
              onClick={() => resetToFolder(item.id)}
            >
              {item.name}
            </Button>
          </span>
        ),
      });
    });

    setBreadcrumbItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    breadcrumbPath,
    draggingDrawingId,
    draggingIds,
    dropTargetId,
    isOwnerOrManager,
    parentFolderId,
  ]);

  const deleteFolderMutation = useMutation({
    mutationFn: ({ folderId }) => deleteDrawingFolder(projectId, folderId),
    onMutate: ({ folderId }) => {
      setDeletingFolderId(folderId);
    },
    onSuccess: () => {
      notification.success({ message: "Drawing folder deleted successfully" });
      return queryClient.invalidateQueries(["drawingFolders", projectId]);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to delete drawing folder",
        description: errorMessage,
      });
    },
    onSettled: () => {
      setDeletingFolderId(null);
    },
  });

  const deleteDrawingMutation = useMutation({
    mutationFn: ({ drawingId }) => deleteDrawing(projectId, drawingId),
    onMutate: ({ drawingId }) => {
      setDeletingDrawingId(drawingId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["drawings", projectId, parentFolderId]);
      notification.success({ message: "Drawing deleted successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to delete drawing",
        description: errorMessage,
      });
    },
    onSettled: () => {
      setDeletingDrawingId(null);
    },
  });

  const updateDrawingFolderMutation = useMutation({
    mutationFn: ({ showLatestRevisionsOnly: value }) =>
      updateDrawingFolder(projectId, parentFolderId, {
        show_latest_revisions_only: value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["drawingFolder", projectId, parentFolderId]);
      queryClient.invalidateQueries(["drawings", projectId, parentFolderId]);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.message ||
        "An unknown error occurred";
      notification.error({
        message: "Failed to update drawing folder",
        description: errorMessage,
      });
    },
  });

  const moveFoldersMutation = useMutation({
    mutationFn: async ({ folderIds, targetParentId }) => {
      const results = [];
      for (const id of folderIds) {
        results.push(await moveDrawingFolder(projectId, id, targetParentId));
      }
      return results;
    },
    onSuccess: (_data, variables) => {
      const count = variables.folderIds.length;
      notification.success({
        message:
          count === 1
            ? "Folder moved successfully"
            : `${count} folders moved successfully`,
      });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to move folder",
        description: errorMessage,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["drawingFolders", projectId] });
      setDraggingIds([]);
      setDropTargetId(null);
    },
  });

  const moveDrawingMutation = useMutation({
    mutationFn: ({ drawingId, targetFolderId }) =>
      moveDrawing(projectId, drawingId, targetFolderId),
    onSuccess: () => {
      notification.success({ message: "Drawing moved successfully" });
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail?.message ||
        error.response?.data?.detail ||
        error.message;
      notification.error({
        message: "Failed to move drawing",
        description: errorMessage,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["drawings", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["drawingFolders", projectId],
      });
      setDraggingDrawingId(null);
      setDropTargetId(null);
    },
  });

  const getIdsToMove = (draggedId) => [String(draggedId)];

  const canDropOntoTarget = (targetFolderId, idsBeingMoved) => {
    if (!targetFolderId || !idsBeingMoved?.length) return false;
    if (idsBeingMoved.includes(targetFolderId)) return false;
    if (parentFolderId && String(targetFolderId) === String(parentFolderId)) {
      return false;
    }
    return true;
  };

  const handleMoveDrop = (targetParentId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedDrawing =
      e.dataTransfer.getData("text/drawing-id") || draggingDrawingId;
    if (draggedDrawing) {
      if (
        targetParentId === "root" ||
        String(targetParentId) === String(parentFolderId)
      ) {
        setDropTargetId(null);
        return;
      }
      moveDrawingMutation.mutate({
        drawingId: draggedDrawing,
        targetFolderId: targetParentId,
      });
      return;
    }

    const draggedId =
      e.dataTransfer.getData("text/drawing-folder-id") || draggingIds[0];
    if (!draggedId) {
      setDropTargetId(null);
      return;
    }
    if (targetParentId === "root" && !parentFolderId) {
      setDropTargetId(null);
      return;
    }
    const idsToMove = getIdsToMove(draggedId).filter(
      (id) => String(id) !== String(targetParentId)
    );
    if (!idsToMove.length || !canDropOntoTarget(targetParentId, idsToMove)) {
      setDropTargetId(null);
      return;
    }
    if (parentFolderId && String(targetParentId) === String(parentFolderId)) {
      setDropTargetId(null);
      return;
    }
    moveFoldersMutation.mutate({
      folderIds: idsToMove,
      targetParentId,
    });
  };

  const handleRowDragStart = (record, e) => {
    if (!isOwnerOrManager || !record.isFolder) {
      e.preventDefault();
      return;
    }
    suppressClickRef.current = true;
    const idsToMove = getIdsToMove(record._id);
    setDraggingIds(idsToMove);
    e.dataTransfer.setData("text/drawing-folder-id", String(record._id));
    e.dataTransfer.effectAllowed = "move";

    const ghost = document.createElement("div");
    ghost.className =
      "fixed -top-[1000px] -left-[1000px] flex items-center gap-2 max-w-[280px] px-3 py-2 bg-white border border-gray-200 rounded-md shadow-md text-sm text-gray-800 pointer-events-none";

    const accent = document.createElement("span");
    accent.className = "inline-block w-2 h-2 rounded-sm shrink-0 bg-amber-400";

    const label = document.createElement("span");
    label.className = "truncate";
    label.textContent =
      idsToMove.length > 1
        ? `Moving ${idsToMove.length} folders`
        : record.name || "Folder";

    ghost.appendChild(accent);
    ghost.appendChild(label);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, 12);
    window.setTimeout(() => {
      if (ghost.parentNode) {
        document.body.removeChild(ghost);
      }
    }, 0);
  };

  const handleRowDragEnd = () => {
    setDraggingIds([]);
    setDraggingDrawingId(null);
    setDropTargetId(null);
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handleDrawingDragStart = (record, e) => {
    if (!isOwnerOrManager || record.isFolder) {
      e.preventDefault();
      return;
    }
    suppressClickRef.current = true;
    setDraggingDrawingId(String(record._id));
    e.dataTransfer.setData("text/drawing-id", String(record._id));
    e.dataTransfer.effectAllowed = "move";

    const ghost = document.createElement("div");
    ghost.className =
      "fixed -top-[1000px] -left-[1000px] flex items-center gap-2 max-w-[280px] px-3 py-2 bg-white border border-gray-200 rounded-md shadow-md text-sm text-gray-800 pointer-events-none";

    const accent = document.createElement("span");
    accent.className = "inline-block w-2 h-2 rounded-sm shrink-0 bg-blue-500";

    const label = document.createElement("span");
    label.className = "truncate";
    label.textContent = record.name || "Drawing";

    ghost.appendChild(accent);
    ghost.appendChild(label);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, 12);
    window.setTimeout(() => {
      if (ghost.parentNode) {
        document.body.removeChild(ghost);
      }
    }, 0);
  };

  const handleUpdateFolderClick = (folder) => {
    setFolderToEdit(folder);
    setIsUpdateFolderModalVisible(true);
  };

  const handleDeleteFolderClick = (folderId) => {
    deleteFolderMutation.mutate({ folderId });
  };

  const handleUpdateDrawingClick = (drawing) => {
    setSelectedDrawing(drawing);
    setIsUpdateDrawingModalVisible(true);
  };

  const handleDeleteDrawingClick = (drawingId) => {
    deleteDrawingMutation.mutate({ drawingId });
  };

  const handleDownloadFolder = async (
    folder,
    showLatestOnly = true
  ) => {
    setDownloadingFolderId(folder._id);
    notification.info({
      message: "Compiling folder files",
      description: "Please wait while we prepare your download...",
      duration: 4.5,
      placement: "topRight",
    });
    try {
      await downloadDrawingFolder(
        projectId,
        folder._id,
        folder.name,
        showLatestOnly
      );
      notification.success({
        message: "Drawing folder downloaded successfully",
        description: showLatestOnly
          ? "Current set (latest revisions only)"
          : "All revisions",
        placement: "topRight",
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data || errorMessage;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      notification.error({
        message: "Failed to download drawing folder",
        description: errorMessage,
        placement: "topRight",
      });
    } finally {
      setDownloadingFolderId(null);
    }
  };

  const handleNotifyUsers = (folder) => {
    setFolderToNotify(folder);
    setIsNotifyUsersModalVisible(true);
  };

  const handleSendNotification = async (userIds, note) => {
    if (!folderToNotify) return;

    setNotifyingFolderId(folderToNotify._id);
    setIsNotifyUsersModalVisible(false);

    try {
      const result = await notifyProjectUsersAboutDrawingFolder(
        projectId,
        folderToNotify._id,
        userIds,
        note
      );
      notification.success({
        message: "Notification sent successfully",
        description: `Email sent to ${result.recipients_count} project user${result.recipients_count !== 1 ? "s" : ""}`,
      });
    } catch (error) {
      let errorMessage = "An unknown error occurred";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          try {
            const errorData = JSON.parse(error.response.data);
            errorMessage =
              errorData?.detail?.message || errorData?.detail || errorMessage;
          } catch {
            errorMessage = error.response.data;
          }
        } else {
          errorMessage =
            error.response.data?.detail?.message ||
            error.response.data?.detail ||
            errorMessage;
        }
      }

      notification.error({
        message: "Failed to send notification",
        description: errorMessage,
      });
    } finally {
      setNotifyingFolderId(null);
      setFolderToNotify(null);
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!totalDrawings || totalDrawings === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });

      const allDrawingsResponse = await fetchDrawings(projectId, parentFolderId, {
        skip: 0,
        limit: totalDrawings,
        filters:
          Object.keys(drawingFilters).length > 0
            ? JSON.stringify(drawingFilters)
            : null,
        show_latest_revisions_only: showLatestRevisionsOnly,
        sort_field: drawingSortField,
        sort_order: sortDirection,
        include_annotations: false,
      });

      exportDrawingsToPDF(
        allDrawingsResponse.drawings,
        projectName,
        currentFolderData?.name
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleMergeAndDownloadPDFs = async () => {
    try {
      if (!totalDrawings || totalDrawings === 0) {
        notification.warning({ message: "No drawings to merge" });
        return;
      }

      setIsMergingPDFs(true);
      notification.info({
        message: "Merging PDFs...",
        description: "System is merging PDFs, please wait...",
        duration: 3,
      });

      await mergeDrawingFolderPDFs(projectId, parentFolderId, {
        show_latest_revisions_only: showLatestRevisionsOnly,
        filters:
          Object.keys(drawingFilters).length > 0
            ? JSON.stringify(drawingFilters)
            : null,
        sort_field: drawingSortField,
        sort_order: sortDirection,
        folderName: currentFolderData?.name,
      });

      notification.success({
        message: "PDFs merged successfully",
        description: "The merged PDF has been downloaded.",
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "An error occurred while merging PDFs";
      notification.error({
        message: "Failed to merge PDFs",
        description: errorMessage,
      });
    } finally {
      setIsMergingPDFs(false);
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    const searchValue = selectedKeys[0];
    setSearchedColumn(dataIndex);
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    if (dataIndex === "name") {
      setFilters((prev) => {
        if (!searchValue) {
          const next = { ...prev };
          delete next.name;
          return next;
        }
        return { ...prev, name: searchValue };
      });
      setDrawingFilters((prev) => {
        if (!searchValue) {
          const next = { ...prev };
          delete next.search;
          return next;
        }
        return {
          ...prev,
          search: searchValue,
        };
      });
    } else if (dataIndex === "revision_title") {
      setDrawingFilters((prev) => {
        if (!searchValue) {
          const next = { ...prev };
          delete next.revision_title;
          return next;
        }
        return { ...prev, revision_title: searchValue };
      });
    }

    confirm();
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters?.();
    setSearchedColumn("");
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    if (dataIndex === "name") {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.name;
        return next;
      });
      setDrawingFilters((prev) => {
        const next = { ...prev };
        delete next.search;
        return next;
      });
    } else if (dataIndex === "revision_title") {
      setDrawingFilters((prev) => {
        const next = { ...prev };
        delete next.revision_title;
        return next;
      });
    }

    confirm();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setDrawingFilters({});
    setSearchedColumn("");
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    setTableKey((prev) => prev + 1);
  };

  const handleTableChange = (newPagination, _filters, sorter, extra) => {
    // Folders table (inside a folder) has pagination={false} but owns the visible
    // header — still wire onChange so sort clicks work. Only update page state
    // when Ant Design actually sends pagination values.
    if (
      newPagination &&
      typeof newPagination === "object" &&
      newPagination.pageSize != null
    ) {
      setPagination((prev) => ({
        ...prev,
        current: newPagination.current ?? prev.current,
        pageSize: newPagination.pageSize,
      }));
      savePreferredPageSize(newPagination.pageSize, "drawing_folders");
    }
    if (extra?.action === "sort") {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }

    const newField = sorter?.order ? sorter.field : null;
    const newOrder =
      sorter?.order === "ascend"
        ? "asc"
        : sorter?.order === "descend"
          ? "desc"
          : null;

    setSortField(newField);
    setSortDirection(newOrder);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            searchInput.current = node;
          }}
          placeholder={`Search ${dataIndex === "name" ? "name" : dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  if (foldersError) {
    notification.error({
      message: "Failed to load drawing folders",
      description: foldersErrorDetails.message,
    });
  }

  if (drawingsError) {
    notification.error({
      message: "Failed to load drawings",
      description: drawingsErrorDetails?.message,
    });
  }

  const renderFolderActions = (record) =>
    deletingFolderId === record._id ? (
      <Spin size="small" />
    ) : (
      <Space>
        {canCreateFolders && (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="1"
                  onClick={() => handleUpdateFolderClick(record)}
                >
                  <Button className="w-full">Update</Button>
                </Menu.Item>
                <Menu.Item key="2">
                  <Popconfirm
                    title="Are you sure you want to delete this folder?"
                    description="This also deletes all nested folders and drawings inside."
                    onConfirm={() => handleDeleteFolderClick(record._id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      className="w-full"
                      danger
                      loading={deletingFolderId === record._id}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button
              icon={<EditOutlined />}
              size="middle"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="current"
                onClick={(e) => {
                  e.domEvent?.stopPropagation();
                  handleDownloadFolder(record, true);
                }}
                disabled={downloadingFolderId === record._id}
              >
                {downloadingFolderId === record._id ? (
                  <span>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    Compiling files...
                  </span>
                ) : (
                  "Latest Revisions Only"
                )}
              </Menu.Item>
              <Menu.Item
                key="all"
                onClick={(e) => {
                  e.domEvent?.stopPropagation();
                  handleDownloadFolder(record, false);
                }}
                disabled={downloadingFolderId === record._id}
              >
                {downloadingFolderId === record._id ? (
                  <span>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    Compiling files...
                  </span>
                ) : (
                  "All Revisions"
                )}
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button
            icon={<DownloadOutlined />}
            onClick={(e) => {
              e.stopPropagation();
            }}
            loading={downloadingFolderId === record._id}
            disabled={downloadingFolderId === record._id}
          />
        </Dropdown>
        <Button
          icon={<MailOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleNotifyUsers(record);
          }}
          loading={notifyingFolderId === record._id}
          disabled={notifyingFolderId === record._id}
          title="Notify project users"
        />
        {isOwnerOrManager && (
          <Button
            icon={<ShareAltOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setFolderToShare(record);
            }}
            title="Share public link"
          />
        )}
      </Space>
    );

  const renderDrawingActions = (record) =>
    deletingDrawingId === record._id ? (
      <Spin size="small" />
    ) : (
      <Space>
        <Button
          icon={<EyeOutlined />}
          title="Preview"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDrawing(record);
          }}
        />
        {canUploadDrawings && (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="update">
                  <Button
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateDrawingClick(record);
                    }}
                  >
                    Update
                  </Button>
                </Menu.Item>
                <Menu.Item key="delete">
                  <Popconfirm
                    title="Are you sure you want to delete this drawing?"
                    onConfirm={() => handleDeleteDrawingClick(record._id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      className="w-full"
                      danger
                      loading={deletingDrawingId === record._id}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button
              icon={<EditOutlined />}
              size="middle"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}
      </Space>
    );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      sorter: true,
      sortOrder:
        sortField === "name" && sortDirection
          ? sortDirection === "asc"
            ? "ascend"
            : "descend"
          : null,
      ...getColumnSearchProps("name"),
      render: (_text, record) => {
        const displayName = record.isFolder
          ? record.name
          : [record.drawing_number, record.title].filter(Boolean).join(" - ") ||
            record.name ||
            "";
        const searchWord =
          filters.name || drawingFilters.search || "";
        const highlighted =
          searchedColumn === "name" && searchWord ? (
            <Highlighter
              highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
              searchWords={[searchWord]}
              autoEscape
              textToHighlight={displayName ? displayName.toString() : ""}
            />
          ) : (
            displayName
          );

        return (
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(record);
            }}
            icon={
              record.isFolder ? (
                <FolderFilled style={{ color: "#faad14" }} />
              ) : record.file_type === "application/pdf" ? (
                <FilePdfOutlined />
              ) : (
                <FileOutlined />
              )
            }
            style={{
              paddingInline: 0,
              height: "auto",
              textAlign: "left",
              maxWidth: "100%",
            }}
          >
            <Typography.Text
              strong={record.isFolder}
              ellipsis
              style={record.isFolder ? { color: "#7a4a00" } : undefined}
            >
              {highlighted}
            </Typography.Text>
          </Button>
        );
      },
    },
    {
      title: "Uploaded",
      dataIndex: "created_at",
      key: "created_at",
      width: 140,
      sorter: isInsideFolder,
      sortOrder:
        sortField === "created_at" && sortDirection
          ? sortDirection === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (text, record) =>
        record.isFolder ? "" : text ? formatDateLocal(text) : "",
    },
    {
      title: "Revision Date",
      dataIndex: "revision_date",
      key: "revision_date",
      width: 140,
      sorter: isInsideFolder,
      sortOrder:
        sortField === "revision_date" && sortDirection
          ? sortDirection === "asc"
            ? "ascend"
            : "descend"
          : null,
      render: (text, record) =>
        record.isFolder ? "" : text ? formatDate(text) : "",
    },
    {
      title: "Revision Title",
      dataIndex: "revision_title",
      key: "revision_title",
      width: 300,
      ellipsis: true,
      ...(isInsideFolder ? getColumnSearchProps("revision_title") : {}),
      render: (text, record) => {
        if (record.isFolder) return "";
        const searchWord = drawingFilters.revision_title || "";
        if (searchedColumn === "revision_title" && searchWord) {
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
              searchWords={[searchWord]}
              autoEscape
              textToHighlight={text ? text.toString() : ""}
            />
          );
        }
        return text || "";
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {record.isFolder
            ? renderFolderActions(record)
            : renderDrawingActions(record)}
        </div>
      ),
    },
  ];

  const isLoading =
    foldersLoading ||
    (isInsideFolder && drawingsLoading) ||
    moveFoldersMutation.isPending ||
    moveDrawingMutation.isPending ||
    deleteFolderMutation.isPending;

  const getFolderRowProps = (record) => {
    const isDragging = draggingIds.includes(record._id);
    const isDropTarget = dropTargetId === record._id;
    const canAcceptDrop =
      isOwnerOrManager &&
      ((draggingIds.length > 0 && !draggingIds.includes(record._id)) ||
        !!draggingDrawingId);

    return {
      draggable: isOwnerOrManager,
      onDragStart: (e) => handleRowDragStart(record, e),
      onDragEnd: handleRowDragEnd,
      onDragOver: (e) => {
        if (!canAcceptDrop) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropTargetId !== record._id) {
          setDropTargetId(record._id);
        }
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) {
          return;
        }
        setDropTargetId((prev) => (prev === record._id ? null : prev));
      },
      onDrop: (e) => handleMoveDrop(record._id, e),
      onClick: (e) => {
        if (
          suppressClickRef.current ||
          e.target.closest("button") ||
          e.target.closest(".ant-space") ||
          e.target.closest(".ant-checkbox-wrapper")
        ) {
          return;
        }
        handleEnterFolder(record);
      },
      style: {
        cursor: isOwnerOrManager ? "grab" : "pointer",
        opacity: isDragging ? 0.45 : 1,
        background: isDropTarget ? "#e6f4ff" : undefined,
        outline: isDropTarget ? "2px solid #1677ff" : undefined,
        outlineOffset: isDropTarget ? -2 : undefined,
        transition: "background 0.15s ease, opacity 0.15s ease",
      },
    };
  };

  const getDrawingRowProps = (record) => ({
    draggable: isOwnerOrManager,
    onDragStart: (e) => handleDrawingDragStart(record, e),
    onDragEnd: handleRowDragEnd,
    onClick: (e) => {
      if (
        e.target.closest("button") ||
        e.target.closest(".ant-space") ||
        e.target.closest(".ant-checkbox-wrapper")
      ) {
        return;
      }
      handleOpenDrawing(record);
    },
    style: {
      cursor: isOwnerOrManager ? "grab" : "pointer",
      opacity: draggingDrawingId === String(record._id) ? 0.45 : 1,
      transition: "opacity 0.15s ease",
    },
  });

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Drawing Folders</h1>
      <div className="mb-4">
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
            {canCreateFolders && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateFolderModalVisible(true)}
              >
                Create Folder
              </Button>
            )}
            {isInsideFolder && canUploadDrawings && (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setIsAIMode(false);
                    setIsCreateDrawingModalVisible(true);
                  }}
                >
                  Add Drawing
                </Button>
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  onClick={() => {
                    setIsAIMode(true);
                    setIsCreateDrawingModalVisible(true);
                  }}
                >
                  AI Bulk Upload
                </Button>
              </>
            )}
            {isInsideFolder && (
              <>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={handleExportPDF}
                  disabled={!drawingsData || drawingsData.length === 0}
                >
                  Export PDF
                </Button>
                <Button
                  icon={<MergeCellsOutlined />}
                  onClick={handleMergeAndDownloadPDFs}
                  disabled={
                    !drawingsData || drawingsData.length === 0 || isMergingPDFs
                  }
                  loading={isMergingPDFs}
                >
                  Merge & Download
                </Button>
                <Checkbox
                  checked={showLatestRevisionsOnly}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setShowLatestRevisionsOnly(newValue);
                    updateDrawingFolderMutation.mutate({
                      showLatestRevisionsOnly: newValue,
                    });
                  }}
                >
                  Show Latest Revisions Only
                </Checkbox>
              </>
            )}
          </div>
          <Button
            icon={<ClearOutlined />}
            onClick={handleClearAllFilters}
            className="shrink-0"
          >
            Clear All Filters
          </Button>
        </div>
        <div className="mt-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>
      <Spin spinning={isLoading}>
        {isInsideFolder && folderTableData.length > 0 && (
          <Table
            key={`folders-${tableKey}`}
            columns={columns}
            dataSource={folderTableData}
            rowKey="key"
            loading={
              foldersLoading ||
              moveFoldersMutation.isPending ||
              moveDrawingMutation.isPending ||
              deleteFolderMutation.isPending
            }
            pagination={false}
            tableLayout="fixed"
            className="mb-4"
            // Header lives on this table when subfolders exist; drawings table
            // below hides its header — sort/filter clicks must land here.
            onChange={handleTableChange}
            onRow={getFolderRowProps}
          />
        )}
        <Table
          key={tableKey}
          columns={columns}
          dataSource={tableData}
          rowKey="key"
          loading={isLoading}
          tableLayout="fixed"
          showHeader={!(isInsideFolder && folderTableData.length > 0)}
          pagination={
            isInsideFolder
              ? {
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: totalDrawings,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: () =>
                    `${foldersData.length} folder${foldersData.length === 1 ? "" : "s"}, ${totalDrawings} drawing${totalDrawings === 1 ? "" : "s"}`,
                }
              : {
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: totalFolders,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }
          }
          onChange={handleTableChange}
          onRow={isInsideFolder ? getDrawingRowProps : getFolderRowProps}
        />
      </Spin>

      <CreateDrawingFolderModal
        visible={isCreateFolderModalVisible}
        onClose={() => setIsCreateFolderModalVisible(false)}
        parentId={parentFolderId}
      />

      <UpdateDrawingFolderModal
        visible={isUpdateFolderModalVisible}
        onClose={() => {
          setIsUpdateFolderModalVisible(false);
          setFolderToEdit(null);
        }}
        folder={folderToEdit}
      />

      <CreateDrawingModal
        visible={isCreateDrawingModalVisible}
        onClose={() => setIsCreateDrawingModalVisible(false)}
        folderId={parentFolderId}
        isAIMode={isAIMode}
        onProcessingStarted={() => setIsAIProcessing(true)}
      />

      <UpdateDrawingModal
        visible={isUpdateDrawingModalVisible}
        onClose={() => {
          setIsUpdateDrawingModalVisible(false);
          setSelectedDrawing(null);
        }}
        drawing={selectedDrawing}
      />

      <NotifyUsersModal
        visible={isNotifyUsersModalVisible}
        onClose={() => {
          setIsNotifyUsersModalVisible(false);
          setFolderToNotify(null);
        }}
        folder={folderToNotify}
        onNotify={handleSendNotification}
        showNote
      />

      <ShareFolderModal
        visible={!!folderToShare}
        onClose={() => setFolderToShare(null)}
        folderName={folderToShare?.name}
        loadShare={() => fetchDrawingFolderShare(projectId, folderToShare._id)}
        enableShare={() => shareDrawingFolder(projectId, folderToShare._id)}
        disableShare={() => unshareDrawingFolder(projectId, folderToShare._id)}
      />
    </div>
  );
};

export default DrawingFolders;
